import * as Constants from '@emi/shared/constants';
import { Brand, ConfigureData, DateRange, ExtractionMutated, Filters } from '@emi/shared/models';
import { createFeature, createReducer, on } from '@ngrx/store';
import * as extractionsActions from './extraction-actions';

export interface DataExtractionState {
    extractions: ExtractionMutated[];
    reloadedExtraction: ExtractionMutated | null;
    selectedFilters: Filters;
    selectedConfiguration: ConfigureData;
    title: string;
    description: string;
    loading: boolean;
    failed: boolean;
    error: string;
}

export const dataExtractionFeatureKey = 'dataExtraction';

const initialState: DataExtractionState = {
    selectedFilters: new Filters(),
    selectedConfiguration: new ConfigureData(),
    reloadedExtraction: null,
    title: '',
    description: '',
    loading: false,
    failed: false,
    error: '',
    extractions: [],
};

export const feature = createFeature({
    name: dataExtractionFeatureKey,
    reducer: createReducer(
        initialState,
        on(extractionsActions.isLoadingExtractions, (state) => {
            return {
                ...state,
                loading: true,
            };
        }),
        on(extractionsActions.getExtractionsSuccess, (state, { extractions }) => {
            const mutatedExtractions = extractions.map((extraction) => {
                return {
                    ...extraction,
                    frequency: extraction.frequency ?? Constants.Application.DEFAULT_FREQUENCY,
                    viewBy: extraction.viewBy ?? Constants.Application.DEFAULT_VIEW_BY,
                    filterNames: {
                        countries: [],
                        categories: [],
                        suppliers: [],
                        brands: [],
                        attributeTypes: [],
                        attributes: [],
                        retailerTypes: [],
                        retailers: [],
                        promotions: [],
                    },
                    isLoaded: false,
                    isDownloading: false,
                };
            });
            return {
                ...initialState,
                extractions: mutatedExtractions,
                loading: false,
                failed: false,
                error: '',
            };
        }),
        on(extractionsActions.getExtractionsFailure, (state, { error }) => {
            return {
                ...state,
                loading: false,
                failed: true,
                error: error.message,
            };
        }),
        on(extractionsActions.getFilterNamesSuccess, (state, { names, extractionId, filterType }) => {
            const mutatedExtractions = state.extractions.map((extraction) => {
                if (extraction.id === extractionId) {
                    return {
                        ...extraction,
                        filterNames: { ...extraction.filterNames, [filterType]: names },
                    };
                }
                return extraction;
            });
            return {
                ...state,
                extractions: mutatedExtractions,
                failed: false,
                error: '',
            };
        }),
        on(extractionsActions.getFilterNamesFailure, (state, { error }) => {
            return {
                ...state,
                loading: false,
                failed: true,
                error: error.message,
            };
        }),
        on(extractionsActions.addTitleInput, (state, { title }) => {
            return {
                ...state,
                title: title,
            };
        }),
        on(extractionsActions.addDescriptionInput, (state, { description }) => {
            return {
                ...state,
                description: description,
            };
        }),
        on(extractionsActions.addConfigureDataSelection, (state, { configureData }) => {
            const updatedSelection = { ...state.selectedFilters };
            // update selection for selected filters (Range & SKU Size)
            updatedSelection.firstSeenPriceRange = configureData.firstSeenPriceRange;
            updatedSelection.lastSeenPriceRange = configureData.lastSeenPriceRange;
            updatedSelection.volumeRange = configureData.volumeRange;
            updatedSelection.weightRange = configureData.weightRange;
            updatedSelection.countRange = configureData.countRange;
            updatedSelection.dateRange = configureData.startDate && configureData.endDate ? new DateRange(configureData.startDate, configureData.endDate) : null;
            
            return {
                ...state,
                selectedConfiguration: configureData,
                selectedFilters: updatedSelection
            };
        }),
        on(extractionsActions.addCountriesSelection, (state, { countries }) => {
            const updatedSelection = { ...state.selectedFilters };
            updatedSelection.countries = countries;
            return {
                ...state,
                selectedFilters: updatedSelection,
            };
        }),
        on(extractionsActions.getSubBrandsSelectionSuccess, (state, { brands }) => {
            const updatedSelection = { ...state.selectedFilters };
            updatedSelection.brands = brands;
            return {
                ...state,
                selectedFilters: updatedSelection,
            };
        }),
        on(extractionsActions.getSubCategoriesSelectionSuccess, (state, { categories }) => {
            const updatedSelection = { ...state.selectedFilters };
            updatedSelection.categories = categories;
            return {
                ...state,
                selectedFilters: updatedSelection,
            };
        }),
        on(extractionsActions.addCategoriesSelection, (state, { categories }) => {
            const updatedSelection = { ...state.selectedFilters };
            updatedSelection.categories = categories;
            return {
                ...state,
                selectedFilters: updatedSelection,
            };
        }),
        on(extractionsActions.addBrandsSelection, (state, { brands }) => {
            const updatedSelection = { ...state.selectedFilters };
            updatedSelection.brands = brands;
            return {
                ...state,
                selectedFilters: updatedSelection,
                loading: false,
            };
        }),
        on(extractionsActions.addCategoriesSuggestionSelection, (state, { categories }) => {
            const updatedSelection = { ...state.selectedFilters };
            updatedSelection.categories = categories;
            return {
                ...state,
                selectedFilters: updatedSelection,
            };
        }),
        on(extractionsActions.addBrandsSuggestionSelection, (state, { brands }) => {
            const updatedSelection = { ...state.selectedFilters };
            updatedSelection.brands = brands;
            return {
                ...state,
                selectedFilters: updatedSelection,
                loading: false,
            };
        }),

        on(extractionsActions.addSubBrandsSuggestionSelection, (state, { subBrands }) => {
            const updatedSelection = { ...state.selectedFilters };
            // temporarily setting the first brand to the first subBrand to avoid null brands with subBrands object
            updatedSelection.brands = [new Brand(subBrands[0].subBrandId, subBrands[0].subBrandName, subBrands)];
            return {
                ...state,
                selectedFilters: updatedSelection,
                loading: false,
            };
        }),
        on(extractionsActions.addSuppliersSelection, (state, { suppliers }) => {
            const updatedSelection = { ...state.selectedFilters };
            updatedSelection.suppliers = suppliers;
            return {
                ...state,
                selectedFilters: updatedSelection,
                loading: false,
            };
        }),
        on(extractionsActions.addAttributeSelection, (state, { attributes }) => {
            const updatedSelection = { ...state.selectedFilters };
            updatedSelection.attributes = attributes;
            return {
                ...state,
                selectedFilters: updatedSelection,
                selectedConfiguration: { ...state.selectedConfiguration, includeAttributesFile: true },
                loading: false,
            };
        }),
        on(extractionsActions.addAttributeTypeSelection, (state, { attributeTypes }) => {
            const updatedSelection = { ...state.selectedFilters };
            updatedSelection.attributeTypes = attributeTypes;
            return {
                ...state,
                selectedFilters: updatedSelection,
                selectedConfiguration: { ...state.selectedConfiguration, includeAttributesFile: true },
                loading: false,
            };
        }),
        on(extractionsActions.addRetailerTypeSelection, (state, { retailerTypes }) => {
            const updatedSelection = { ...state.selectedFilters };
            updatedSelection.retailerTypes = retailerTypes;
            return {
                ...state,
                selectedFilters: updatedSelection,
                loading: false,
            };
        }),
        on(extractionsActions.addRetailerSelection, (state, { retailers }) => {
            const updatedSelection = { ...state.selectedFilters };
            updatedSelection.retailers = retailers;
            return {
                ...state,
                selectedFilters: updatedSelection,
                loading: false,
            };
        }),
        on(extractionsActions.addPromotionSelection, (state, { promotions }) => {
            const updatedSelection = { ...state.selectedFilters };
            updatedSelection.promotions = promotions;
            return {
                ...state,
                selectedFilters: updatedSelection,
                loading: false,
            };
        }),
        on(extractionsActions.addFirstSeenPriceRangeSelection, (state, { firstSeenPriceRange }) => {
            const updatedSelection = { ...state.selectedFilters };
            updatedSelection.firstSeenPriceRange = firstSeenPriceRange;
            return {
                ...state,
                selectedFilters: updatedSelection,
                loading: false,
            };
        }),
        on(extractionsActions.addLastSeenPriceRangeSelection, (state, { lastSeenPriceRange }) => {
            const updatedSelection = { ...state.selectedFilters };
            updatedSelection.lastSeenPriceRange = lastSeenPriceRange;
            return {
                ...state,
                selectedFilters: updatedSelection,
                loading: false,
            };
        }),
        on(extractionsActions.addDateRangeSelection, (state, { dateRange }) => {
            const updatedSelection = { ...state.selectedFilters };
            updatedSelection.dateRange = dateRange;
            return {
                ...state,
                selectedFilters: updatedSelection,
                loading: false,
            };
        }),
        on(extractionsActions.addVolumeRangeSelection, (state, { volumeRange }) => {
            const updatedSelection = { ...state.selectedFilters };
            updatedSelection.volumeRange = volumeRange;
            return {
                ...state,
                selectedFilters: updatedSelection,
                loading: false,
            };
        }),
        on(extractionsActions.addWeightRangeSelection, (state, { weightRange }) => {
            const updatedSelection = { ...state.selectedFilters };
            updatedSelection.weightRange = weightRange;
            return {
                ...state,
                selectedFilters: updatedSelection,
                loading: false,
            };
        }),
        on(extractionsActions.addCountRangeSelection, (state, { countRange }) => {
            const updatedSelection = { ...state.selectedFilters };
            updatedSelection.countRange = countRange;
            return {
                ...state,
                selectedFilters: updatedSelection,
                loading: false,
            };
        }),
        on(extractionsActions.getCountriesFiltersSuccess, (state, { countries }) => {
            const updatedSelection = { ...state.selectedFilters };
            updatedSelection.countries = countries;
            return {
                ...state,
                selectedFilters: updatedSelection,
            };
        }),
        on(extractionsActions.getCategoriesFiltersSuccess, (state, { categories }) => {
            const updatedSelection = { ...state.selectedFilters };
            updatedSelection.categories = categories;
            return {
                ...state,
                selectedFilters: updatedSelection,
            };
        }),
        on(extractionsActions.getBrandsFiltersSuccess, (state, { brands }) => {
            const updatedSelection = { ...state.selectedFilters };
            updatedSelection.brands = brands;
            return {
                ...state,
                selectedFilters: updatedSelection,
            };
        }),
        on(extractionsActions.getSuppliersFiltersSuccess, (state, { suppliers }) => {
            const updatedSelection = { ...state.selectedFilters };
            updatedSelection.suppliers = suppliers;
            return {
                ...state,
                selectedFilters: updatedSelection,
            };
        }),
        on(extractionsActions.getAttributeFiltersSuccess, (state, { attributes }) => {
            const updatedSelection = { ...state.selectedFilters };
            updatedSelection.attributes = attributes;
            return {
                ...state,
                selectedFilters: updatedSelection,
            };
        }),
        on(extractionsActions.getAttributeTypeFiltersSuccess, (state, { attributeTypes }) => {
            const updatedSelection = { ...state.selectedFilters };
            updatedSelection.attributeTypes = attributeTypes;
            return {
                ...state,
                selectedFilters: updatedSelection,
            };
        }),
        on(extractionsActions.getRetailerTypeFiltersSuccess, (state, { retailerTypes }) => {
            const updatedSelection = { ...state.selectedFilters };
            updatedSelection.retailerTypes = retailerTypes;
            return {
                ...state,
                selectedFilters: updatedSelection,
            };
        }),
        on(extractionsActions.getRetailerFiltersSuccess, (state, { retailers }) => {
            const updatedSelection = { ...state.selectedFilters };
            updatedSelection.retailers = retailers;
            return {
                ...state,
                selectedFilters: updatedSelection,
            };
        }),
        on(extractionsActions.getPromotionFiltersSuccess, (state, { promotions }) => {
            const updatedSelection = { ...state.selectedFilters };
            updatedSelection.promotions = promotions;
            return {
                ...state,
                selectedFilters: updatedSelection,
            };
        }),
        on(extractionsActions.clearSelections, () => {
            return initialState;
        }),
        on(extractionsActions.isLoadingSelections, (state, { isLoading }) => {
            return {
                ...state,
                loading: isLoading,
            };
        }),
        on(extractionsActions.isLoadingExtractionFilterNames, (state, { extractionId, isLoading }) => {
            const mutatedExtractions = state.extractions.map((extraction) => {
                if (extraction.id === extractionId) {
                    return {
                        ...extraction,
                        isLoaded: true,
                    };
                }
                return extraction;
            });
            return {
                ...state,
                extractions: mutatedExtractions,
                loading: isLoading,
            };
        }),
        on(extractionsActions.deleteExtractionsSuccess, (state, { extractionId }) => {
            const index = state.extractions.findIndex((x) => x.id == extractionId);
            const mutatedExtractions = [...state.extractions];
            mutatedExtractions.splice(index, 1);
            return {
                ...state,
                extractions: mutatedExtractions,
                loading: false,
                failed: false,
                error: '',
            };
        }),
        on(extractionsActions.deleteExtractionsFailure, (state, { error }) => {
            return {
                ...state,
                loading: false,
                failed: true,
                error: error.message,
            };
        }),
        on(extractionsActions.downloadExtraction, (state, { extractionId }) => {
            const mutatedExtractions = state.extractions.map((extraction) => {
                if (extraction.id === extractionId) {
                    return {
                        ...extraction,
                        isDownloading: true,
                    };
                }
                return extraction;
            });
            return {
                ...state,
                extractions: mutatedExtractions,
                loading: false,
            };
        }),
        on(extractionsActions.downloadExtractionSuccess, (state, { extractionId }) => {
            const mutatedExtractions = state.extractions.map((extraction) => {
                if (extraction.id === extractionId) {
                    return {
                        ...extraction,
                        isDownloading: false,
                    };
                }
                return extraction;
            });
            return {
                ...state,
                extractions: mutatedExtractions,
                loading: false,
                failed: false,
                error: '',
            };
        }),
        on(extractionsActions.downloadExtractionsFailure, (state, { error, extractionId }) => {
            const mutatedExtractions = state.extractions.map((extraction) => {
                if (extraction.id === extractionId) {
                    return {
                        ...extraction,
                        isDownloading: false,
                    };
                }
                return extraction;
            });
            return {
                ...state,
                extractions: mutatedExtractions,
                loading: false,
                failed: true,
                error: error.message,
            };
        }),
        on(extractionsActions.postExtractions, (state) => {
            return {
                ...state,
                loading: true,
            };
        }),
        on(extractionsActions.postExtractionsSuccess, (state) => {
            return {
                ...state,
                loading: false,
            };
        }),
        on(extractionsActions.getExchangeRatesSuccess, (state, { exchangeRates }) => {
            return {
                ...state,
                selectedConfiguration: { ...state.selectedConfiguration, exchangeRates: exchangeRates },
            };
        }),
        on(extractionsActions.updateReloadedExtraction, (state, { reloadedExtraction }) => {
            return {
                ...state,
                reloadedExtraction: reloadedExtraction,
            };
        }),
        on(extractionsActions.updateDefaultDateRange, (state, { defaultDateRange }) => {
            const updatedFilters = { ...state.selectedFilters };
            updatedFilters.defaultDateRange = defaultDateRange;
            return {
                ...state,
                selectedFilters: updatedFilters,
            };
        })
    ),
});
